export type SkillSubtypeOption = {
  value: string;
  label: string;
  params: Record<string, unknown>;
};

export const GRAPH_FUNCTION_TYPES: SkillSubtypeOption[] = [
  { value: 'linear', label: 'Linear', params: { function_types: ['linear'] } },
  { value: 'quadratic', label: 'Quadratic', params: { function_types: ['quadratic'] } },
  { value: 'cubic', label: 'Cubic', params: { function_types: ['cubic'] } },
  { value: 'quartic', label: 'Quartic', params: { function_types: ['quartic'] } },
  { value: 'exponential', label: 'Exponential', params: { function_types: ['exponential'] } },
  { value: 'logarithmic', label: 'Logarithmic', params: { function_types: ['logarithmic'] } },
  { value: 'rational', label: 'Rational', params: { function_types: ['rational'] } },
  { value: 'absolute', label: 'Absolute', params: { function_types: ['absolute'] } },
  { value: 'sqrt', label: 'Square root', params: { function_types: ['sqrt'] } },
  { value: 'cuberoot', label: 'Cube root', params: { function_types: ['cuberoot'] } },
  { value: 'sin', label: 'Sine', params: { function_types: ['sin'] } },
  { value: 'cos', label: 'Cosine', params: { function_types: ['cos'] } },
  { value: 'tan', label: 'Tangent', params: { function_types: ['tan'] } },
];

export const GRAPH_TRANSFORMATION_COUNTS = ['1', '2', '3', '3+'] as const;
export type GraphTransformationCount = (typeof GRAPH_TRANSFORMATION_COUNTS)[number];

export const SKILL_SUBTYPE_OPTIONS: Record<string, SkillSubtypeOption[]> = {
  differentiation: [
    { value: 'simple', label: 'Simple', params: { include_simple: true } },
    { value: 'chain', label: 'Chain rule', params: { include_chain: true } },
    { value: 'product', label: 'Product rule', params: { include_product: true } },
    { value: 'quotient', label: 'Quotient rule', params: { include_quotient: true } },
  ],
  trigonometry: [
    { value: 'sin', label: 'Sine', params: { include_sin: true } },
    { value: 'cos', label: 'Cosine', params: { include_cos: true } },
    { value: 'tan', label: 'Tangent', params: { include_tan: true } },
  ],
  integration: [
    { value: 'simple', label: 'Basic', params: { include_simple: true, include_constant: true } },
    { value: 'substitution', label: 'Substitution', params: { include_chain: true, include_constant: true } },
    { value: 'parts', label: 'By parts', params: { include_product: true, include_constant: true } },
    { value: 'partial_fractions', label: 'Partial fractions', params: { include_quotient: true, include_constant: true } },
  ],
  logarithms: [
    { value: 'linear', label: 'Linear inner term', params: { include_linear: true } },
    { value: 'power', label: 'Power inner term', params: { include_power: true } },
  ],
  exponential: [
    { value: 'simple', label: 'Simple', params: { include_simple: true } },
    { value: 'linear', label: 'Linear exponent', params: { include_linear: true } },
    { value: 'different_bases', label: 'Different bases', params: { include_different_bases: true } },
    { value: 'sum', label: 'Sum', params: { include_sum: true } },
    { value: 'reciprocal', label: 'Reciprocal', params: { include_reciprocal: true } },
    { value: 'polynomial', label: 'Polynomial exponent', params: { include_polynomial: true } },
  ],
  quadratic: [
    { value: 'monic', label: 'Monic', params: { include_monic: true } },
    { value: 'non_monic', label: 'Non-monic', params: { include_non_monic: true } },
  ],
  cubic: [{ value: 'mixed', label: 'Mixed cubic', params: {} }],
  algebra: [
    { value: 'expansion', label: 'Expansion', params: { include_expansion: true } },
    { value: 'factorization', label: 'Factorisation', params: { include_factorization: true } },
    { value: 'linear_equations', label: 'Linear equations', params: { include_linear_equations: true } },
    { value: 'advanced_linear', label: 'Advanced linear', params: { include_advanced_linear_equations: true } },
  ],
  transformations: [
    { value: 'apply', label: 'Apply transformations', params: { include_apply_transformations: true } },
    { value: 'find', label: 'Find transformations', params: { include_find_transformations: true } },
  ],
  graph_sketching: GRAPH_FUNCTION_TYPES,
};

export const getSkillSubtypeOptions = (skillSlug: string): SkillSubtypeOption[] =>
  SKILL_SUBTYPE_OPTIONS[skillSlug] ?? [];

export const getInitialSelectedSkillSubtypes = (skillSlug: string): string[] =>
  getSkillSubtypeOptions(skillSlug).map((option) => option.value);

export const toggleSelectedSkillSubtype = (selectedValues: string[], value: string): string[] => {
  if (selectedValues.includes(value)) {
    return selectedValues.length === 1 ? selectedValues : selectedValues.filter((item) => item !== value);
  }
  return [...selectedValues, value];
};

export const calculateSkillBatchSize = (
  selectedSubtypeCount: number,
  minimumBatchSize = 10,
  backendMaximum = 50
): number => {
  if (selectedSubtypeCount <= 1) {
    return minimumBatchSize;
  }
  const batchSize = Math.ceil(minimumBatchSize / selectedSubtypeCount) * selectedSubtypeCount;
  return Math.min(batchSize, backendMaximum);
};

export const getSelectedSubtypeLabels = (skillSlug: string, selectedValues: string[]): string[] => {
  const selectedSet = new Set(selectedValues);
  return getSkillSubtypeOptions(skillSlug)
    .filter((option) => selectedSet.has(option.value))
    .map((option) => option.label);
};

export const formatSelectedSubtypeLabel = (skillSlug: string, selectedValues: string[]): string => {
  const labels = getSelectedSubtypeLabels(skillSlug, selectedValues);
  if (!labels.length) {
    return 'Mixed';
  }
  if (labels.length <= 2) {
    return labels.join(' + ');
  }
  return `${labels.length} types selected`;
};

export const buildSkillSubtypeParams = (
  skillSlug: string,
  selectedValues: string[],
  numberOfProblems: number,
  transformationCount?: GraphTransformationCount
): Record<string, unknown> => {
  const options = getSkillSubtypeOptions(skillSlug);
  const selectedSet = new Set(selectedValues);

  if (skillSlug === 'graph_sketching') {
    return {
      number_of_problems: numberOfProblems,
      function_types: options.filter((option) => selectedSet.has(option.value)).map((option) => option.value),
      ...(transformationCount ? { transformation_count: transformationCount } : {}),
    };
  }

  const subtypeParams: Record<string, unknown> = {};
  options.forEach((option) => {
    Object.entries(option.params).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        subtypeParams[key] = false;
      }
    });
  });

  options
    .filter((option) => selectedSet.has(option.value))
    .forEach((option) => {
      Object.assign(subtypeParams, option.params);
    });

  return {
    number_of_problems: numberOfProblems,
    ...subtypeParams,
  };
};
